/** Manual Journals (design §9) — append-only entry list; New opens the modal.
 *  Premium pass: search + source-filter tabs, titled card with gradient header,
 *  hover-reactive rows with source-colored chips, and a chip footer. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Platform, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useBaseCurrency } from '../context/currencyContext';
import { useResponsive, MAX_CONTENT_WIDTH } from '../utils/responsive';
import { useSession } from '../context/sessionContext';
import SkIcon from '../components/skIcon';
import { showToast } from '../components/skToast';
import ModalShell from '../components/modals/modalShell';
import ManualJournalModal from '../components/modals/manualJournalModal';
import { listJournals } from '../services/billing/journal.service';
import { getTrialBalance } from '../services/billing/accountant.service';
import { JournalLine } from '../schema/billing/journal';
import { fmtMoney, money } from '../utils/format';

interface Entry {
  id: string; fullId: string; narration: string; source: string; date: string; amount: string; amountNum: number;
  lines: JournalLine[]; createdAt: string; reverses: string | null; reasonCode: string | null;
}

const SOURCE_LABEL: Record<string, string> = { MANUAL: 'Manual', REVERSAL: 'Reversal', DOCUMENT: 'Document', PAYMENT: 'Payment' };

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});
const CTA_A = '#6366f1';
const CTA_B = '#8b5cf6';

type Tone = { soft: string; ink: string; line: string };
const sourceTone = (source: string, colors: any): Tone => {
  switch (source) {
    case 'Payment': return { soft: colors.successSoft, ink: colors.success, line: colors.successLine };
    case 'Document': return { soft: colors.infoSoft, ink: colors.info, line: colors.infoLine };
    case 'Reversal': return { soft: colors.warnSoft, ink: colors.warn, line: colors.warnLine };
    case 'Manual': return { soft: colors.accentSoft, ink: colors.accentInk, line: colors.accentLine };
    default: return { soft: colors.neutralSoft, ink: colors.neutral, line: colors.neutralLine };
  }
};

type Col = (key: string, align?: 'left' | 'right' | 'center') => any;

/** Interactive KPI card — hover-lifts and filters the list on press. */
const StatCard: React.FC<{ label: string; value: string; note: string; icon: string; ink: string; soft: string; line: string; active: boolean; onPress: () => void }> = ({ label, value, note, icon, ink, soft, line, active, onPress }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      style={{
        flex: 1, minWidth: 170, flexDirection: 'row', alignItems: 'center', gap: 13, paddingVertical: 14, paddingHorizontal: 16,
        borderRadius: 14, borderWidth: 1, borderColor: active ? line : hover ? colors.lineStrong : colors.line,
        backgroundColor: active ? soft : colors.card, ...theme.shadow(hover ? 'md' : 'sm'),
        ...web({ transition: 'transform .16s ease, box-shadow .16s ease, border-color .16s ease', transform: hover ? 'translateY(-2px)' : 'translateY(0)', cursor: 'pointer', ...(active ? { backgroundImage: `linear-gradient(145deg, ${soft}, ${colors.card})` } : {}) }),
      }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <View style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: soft, borderWidth: 1, borderColor: line, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${soft}, ${colors.card})`, boxShadow: `0 4px 10px -3px ${ink}40` }) }}>
        <SkIcon name={icon} size={18} color={ink} strokeWidth={2.1} />
      </View>
      <View style={{ minWidth: 0, flex: 1 }}>
        <Text style={{ fontFamily: font.sansMedium, fontSize: 11.5, fontWeight: '500', color: colors.ink3 }}>{label}</Text>
        <Text numberOfLines={1} style={{ fontFamily: font.monoSemibold, fontSize: 20, fontWeight: '600', color: colors.ink, letterSpacing: -0.4, marginTop: 2 }}>{value}</Text>
        <Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4, marginTop: 2 }}>{note}</Text>
      </View>
    </Pressable>
  );
};

/** Hover-reactive journal row with staggered entrance + source-colored edge. */
const JournalRow: React.FC<{ e: Entry; index: number; col: Col; onOpen: () => void }> = ({ e, index, col, onOpen }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  const tone = sourceTone(e.source, colors);
  const a = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(a, { toValue: 1, duration: 360, delay: Math.min(index, 14) * 38, easing: Easing.out(Easing.cubic), useNativeDriver: !isWeb }).start();
  }, [a, index]);
  return (
    <Animated.View style={{ opacity: a, transform: [{ translateY: a.interpolate({ inputRange: [0, 1], outputRange: [9, 0] }) }] }}>
      <Pressable
        onPress={onOpen}
        style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: colors.line, backgroundColor: hover ? colors.paper2 : colors.card, borderLeftWidth: 3, borderLeftColor: hover ? tone.ink : 'transparent', ...web({ transition: 'background-color .12s ease, border-color .12s ease', cursor: 'pointer' }) }}
        {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
      >
        <View style={col('entry')}><Text style={{ fontFamily: font.monoSemibold, fontSize: 12.5, fontWeight: '600', color: colors.ink }}>{e.id}</Text></View>
        <View style={col('narration')}><Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>{e.narration}</Text></View>
        <View style={col('source')}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: tone.soft, borderWidth: 1, borderColor: tone.line }}>
            <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: tone.ink }} />
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600', color: tone.ink }}>{e.source}</Text>
          </View>
        </View>
        <View style={col('date', 'center')}><Text style={{ fontFamily: font.mono, fontSize: 12.5, color: colors.ink3 }}>{e.date}</Text></View>
        <View style={col('amount', 'right')}><Text style={{ fontFamily: font.monoSemibold, fontSize: 13.5, fontWeight: '600', color: colors.ink, letterSpacing: -0.2 }}>{e.amount}</Text></View>
      </Pressable>
    </Animated.View>
  );
};

const Journals: React.FC = () => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const { isWide, isMobile } = useResponsive();
  const { role } = useSession();
  const [modal, setModal] = useState(false);
  const [ENTRIES, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('all');
  const [newHover, setNewHover] = useState(false);
  const [sel, setSel] = useState<Entry | null>(null);
  const [accounts, setAccounts] = useState<Record<string, { code: string; name: string }>>({});

  // account_id → {code, name} so the detail card shows real account names.
  useEffect(() => {
    let alive = true;
    getTrialBalance()
      .then((r: any) => { if (alive) setAccounts(Object.fromEntries((r?.accounts || []).map((a: any) => [a.id, { code: a.code, name: a.name }]))); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    listJournals()
      .then((rows: any[]) => {
        if (!alive) return;
        setEntries(
          (Array.isArray(rows) ? rows : []).map((j) => {
            const lines: JournalLine[] = Array.isArray(j.lines) ? j.lines : [];
            const debit = lines.reduce((s: number, l: any) => s + money(l.debit), 0);
            return {
              id: (j.id || '').slice(0, 8) || 'JE',
              fullId: j.id || '',
              narration: j.narration || '—',
              source: SOURCE_LABEL[j.source_type] || j.source_type || 'Manual',
              date: j.entry_date || '',
              amount: fmtMoney(debit, cur),
              amountNum: debit,
              lines,
              createdAt: j.created_at || '',
              reverses: j.reverses_entry_id || null,
              reasonCode: j.reason_code || null,
            };
          }),
        );
      })
      .catch(() => {
        if (alive) setEntries([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  // Source-filter tabs — All + each source that actually appears.
  const tabs = useMemo(() => {
    const present = Array.from(new Set(ENTRIES.map((e) => e.source)));
    const order = ['Document', 'Payment', 'Manual', 'Reversal'];
    const sorted = present.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    return [{ id: 'all', label: 'All', count: ENTRIES.length }, ...sorted.map((s) => ({ id: s, label: s, count: ENTRIES.filter((e) => e.source === s).length }))];
  }, [ENTRIES]);

  const rows = useMemo(() => {
    let r = tab === 'all' ? ENTRIES : ENTRIES.filter((e) => e.source === tab);
    const q = query.trim().toLowerCase();
    if (q) r = r.filter((e) => (e.narration + ' ' + e.id + ' ' + e.source).toLowerCase().includes(q));
    return r;
  }, [ENTRIES, tab, query]);

  const posted = rows.reduce((s, e) => s + e.amountNum, 0);
  const totalPosted = ENTRIES.reduce((s, e) => s + e.amountNum, 0);
  const docCount = ENTRIES.filter((e) => e.source === 'Document').length;
  const payCount = ENTRIES.filter((e) => e.source === 'Payment').length;
  const kpis = [
    { label: 'Total posted', value: fmtMoney(totalPosted, cur), note: `${ENTRIES.length} entries`, icon: 'rupee', ink: colors.accent, soft: colors.accentSoft, line: colors.accentLine, tab: 'all' },
    { label: 'From documents', value: String(docCount), note: 'invoices · bills · notes', icon: 'fileText', ink: colors.info, soft: colors.infoSoft, line: colors.infoLine, tab: 'Document' },
    { label: 'From payments', value: String(payCount), note: 'receipts · allocations', icon: 'wallet', ink: colors.success, soft: colors.successSoft, line: colors.successLine, tab: 'Payment' },
  ];

  const card = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, ...theme.shadow('sm') } as const;
  const th = { fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600' as const, color: colors.ink4, letterSpacing: 0.6, textTransform: 'uppercase' as const };

  const colCfg: Record<string, { w: number; flex: number }> = {
    entry: { w: 130, flex: 1.2 }, narration: { w: 300, flex: 3 }, source: { w: 120, flex: 1.1 }, date: { w: 130, flex: 1.1 }, amount: { w: 130, flex: 1.1 },
  };
  const col: Col = (key, align = 'left') => {
    const c = colCfg[key];
    const center = align === 'center';
    const base = { paddingHorizontal: 16, paddingVertical: 13, justifyContent: 'center' as const, alignItems: center ? ('center' as const) : align === 'right' ? ('flex-end' as const) : ('flex-start' as const) };
    return isMobile ? { ...base, width: c.w } : { ...base, flex: c.flex };
  };
  const tableMin = Object.values(colCfg).reduce((a, c) => a + c.w, 0);

  const footChip = (label: string, accent = false) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 5, paddingHorizontal: 11, borderRadius: 999, borderWidth: 1, borderColor: accent ? colors.accentLine : colors.line, backgroundColor: accent ? colors.accentSoft : colors.card }}>
      {accent && <SkIcon name="rupee" size={12} color={colors.accentInk} strokeWidth={2.2} />}
      <Text style={{ fontFamily: accent ? font.sansSemibold : font.sansMedium, fontSize: 11.5, fontWeight: accent ? '600' : '500', color: accent ? colors.accentInk : colors.ink3 }}>{label}</Text>
    </View>
  );

  const openNew = () => {
    if (role !== 'ADMIN') { showToast('warn', 'Admin only', 'Manual journals require the ADMIN role'); return; }
    setModal(true);
  };

  return (
    <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16 }}>
      {/* KPI stat strip — tap to filter by source */}
      {!loading && ENTRIES.length > 0 && (
        <View style={{ flexDirection: 'row', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          {kpis.map((k) => (
            <StatCard key={k.label} label={k.label} value={k.value} note={k.note} icon={k.icon} ink={k.ink} soft={k.soft} line={k.line} active={tab === k.tab} onPress={() => setTab(tab === k.tab ? 'all' : k.tab)} />
          ))}
        </View>
      )}

      {/* Search + New journal */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <View style={{ width: 300, maxWidth: '55%', position: 'relative' }}>
          <View style={{ position: 'absolute', left: 11, top: 0, bottom: 0, justifyContent: 'center', zIndex: 1 }}>
            <SkIcon name="search" size={14} color={colors.ink4} strokeWidth={2} />
          </View>
          <TextInput value={query} onChangeText={setQuery} placeholder="Search narration, entry…" placeholderTextColor={colors.ink4} style={{ height: 36, paddingLeft: 34, paddingRight: 12, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, borderRadius: 9, fontSize: 12.5, color: colors.ink, fontFamily: font.sans, ...theme.shadow('sm'), ...web({ outlineStyle: 'none' }) }} />
        </View>
        <View style={{ flex: 1 }} />
        <Pressable
          onPress={openNew}
          style={{ height: 40, paddingHorizontal: 16, borderRadius: 10, backgroundColor: CTA_A, flexDirection: 'row', alignItems: 'center', gap: 7, ...web({ backgroundImage: `linear-gradient(145deg, ${CTA_A}, ${CTA_B})`, boxShadow: newHover ? `0 10px 22px -4px ${CTA_B}aa` : `0 5px 12px -3px ${CTA_B}77`, transform: newHover ? 'translateY(-1px)' : 'translateY(0)', transition: 'transform .14s ease, box-shadow .14s ease', cursor: 'pointer' }) }}
          {...(isWeb ? { onMouseEnter: () => setNewHover(true), onMouseLeave: () => setNewHover(false) } : {})}
        >
          <SkIcon name="plus" size={15} color="#fff" strokeWidth={2.4} />
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: '#fff' }}>New journal</Text>
        </Pressable>
      </View>

      {/* Source-filter tabs */}
      <View style={{ flexDirection: 'row', marginBottom: 16 }}>
        <View style={{ flexDirection: 'row', gap: 3, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, borderRadius: 10, padding: 4, flexWrap: 'wrap' }}>
          {tabs.map((t) => {
            const active = t.id === tab;
            return (
              <Pressable key={t.id} onPress={() => setTab(t.id)} style={{ height: 30, paddingHorizontal: 13, borderRadius: 7, justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 7, backgroundColor: active ? colors.card : 'transparent', ...(active ? web({ boxShadow: '0 1px 3px rgba(16,24,40,.10)' }) : {}), ...web({ transition: 'background-color .12s ease', cursor: 'pointer' }) }}>
                <Text style={{ fontFamily: active ? font.sansSemibold : font.sansMedium, fontSize: 12.5, fontWeight: active ? '600' : '500', color: active ? colors.ink : colors.ink3 }}>{t.label}</Text>
                <View style={{ minWidth: 18, height: 17, paddingHorizontal: 5, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: active ? colors.accentSoft : colors.paper3 }}>
                  <Text style={{ fontFamily: font.monoMedium, fontSize: 10, color: active ? colors.accentInk : colors.ink4 }}>{t.count}</Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      </View>

      {/* Table card */}
      <View style={[card, { overflow: 'hidden' }]}>
        {/* Titled header */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 14, paddingHorizontal: 16, borderBottomWidth: 1, borderColor: colors.line }}>
          <View style={{ width: 32, height: 32, borderRadius: 10, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 3px 8px -3px ${colors.accent}55` }) }}>
            <SkIcon name="journal" size={16} color={colors.accentInk} strokeWidth={2} />
          </View>
          <View style={{ minWidth: 0 }}>
            <Text style={{ fontFamily: font.sansBold, fontSize: 14.5, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>Journal entries</Text>
            <Text style={{ fontFamily: font.sans, fontSize: 11.5, color: colors.ink4, marginTop: 1 }}>Append-only ledger — post a reversal to correct.</Text>
          </View>
          <View style={{ flex: 1 }} />
          {!loading && (
            <View style={{ paddingHorizontal: 9, height: 22, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontFamily: font.monoMedium, fontSize: 11, color: colors.ink3 }}>{ENTRIES.length} total</Text>
            </View>
          )}
        </View>

        {loading ? (
          <View style={{ padding: 48, alignItems: 'center' }}><Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3 }}>Loading…</Text></View>
        ) : rows.length === 0 ? (
          <View style={{ paddingVertical: 64, paddingHorizontal: 24, alignItems: 'center' }}>
            <View style={{ width: 96, height: 96, borderRadius: 30, alignItems: 'center', justifyContent: 'center', marginBottom: 20, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, ...web({ backgroundImage: `radial-gradient(circle at 50% 32%, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 14px 38px -10px ${colors.accent}4d` }) }}>
              <View style={{ width: 58, height: 58, borderRadius: 19, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.card, borderWidth: 1, borderColor: colors.accentLine, ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 6px 16px -6px ${colors.accent}66` }) }}>
                <SkIcon name="journal" size={26} color={colors.accentInk} strokeWidth={2} />
              </View>
            </View>
            <Text style={{ fontFamily: font.sansBold, fontWeight: '700', fontSize: 18, color: colors.ink, letterSpacing: -0.3, textAlign: 'center' }}>{query.trim() ? 'No matches found' : 'No journal entries'}</Text>
            <Text style={{ fontFamily: font.sans, fontSize: 13.5, color: colors.ink3, marginTop: 6, textAlign: 'center', maxWidth: 400, lineHeight: 20 }}>{query.trim() ? `No entries match “${query.trim()}”. Try a different word or clear the search.` : 'Issuing documents and recording payments post entries here automatically.'}</Text>
          </View>
        ) : (
          <ScrollView horizontal={isMobile} showsHorizontalScrollIndicator={false}>
            <View style={{ width: isMobile ? tableMin : '100%' }}>
              <View style={{ flexDirection: 'row', backgroundColor: colors.paper2, borderBottomWidth: 1, borderColor: colors.line, ...web({ backgroundImage: `linear-gradient(180deg, ${colors.paper2}, ${colors.paper})` }) }}>
                <View style={col('entry')}><Text style={th}>Entry</Text></View>
                <View style={col('narration')}><Text style={th}>Narration</Text></View>
                <View style={col('source')}><Text style={th}>Source</Text></View>
                <View style={col('date', 'center')}><Text style={th}>Date</Text></View>
                <View style={col('amount', 'right')}><Text style={th}>Amount</Text></View>
              </View>
              {rows.map((e, i) => <JournalRow key={e.id} e={e} index={i} col={col} onOpen={() => setSel(e)} />)}
            </View>
          </ScrollView>
        )}

        {/* Footer chips */}
        {!loading && rows.length > 0 && (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, paddingHorizontal: 16, borderTopWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2, flexWrap: 'wrap' }}>
            {footChip(`${rows.length} shown`)}
            {footChip(`${fmtMoney(posted, cur)} posted`, true)}
          </View>
        )}
      </View>

      <JournalDetailModal visible={sel != null} e={sel} accounts={accounts} onClose={() => setSel(null)} />

      <ManualJournalModal visible={modal} onClose={() => setModal(false)} />
    </View>
  );
};

/* ── Premium journal detail card ─────────────────────────────────────── */
const JournalDetailModal: React.FC<{ visible: boolean; e: Entry | null; accounts: Record<string, { code: string; name: string }>; onClose: () => void }> = ({ visible, e, accounts, onClose }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  if (!e) return null;
  const tone = sourceTone(e.source, colors);
  const debitTotal = e.lines.reduce((s, l) => s + money(l.debit), 0);
  const creditTotal = e.lines.reduce((s, l) => s + money(l.credit), 0);
  const balanced = Math.abs(debitTotal - creditTotal) < 0.005;
  const acctLabel = (id: string) => accounts[id]?.name || id;
  const acctCode = (id: string) => accounts[id]?.code || '';

  return (
    <ModalShell visible={visible} onClose={onClose} title="Journal entry" subtitle="Append-only ledger entry" width={520} headerTone="accent" compact
      footer={
        <Pressable onPress={onClose} style={{ height: 38, paddingHorizontal: 18, borderWidth: 1, borderColor: colors.line, borderRadius: 10, justifyContent: 'center', backgroundColor: colors.card, ...(isWeb ? ({ cursor: 'pointer' } as any) : {}) }}>
          <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>Close</Text>
        </Pressable>
      }
    >
      <View style={{ gap: 14 }}>
        {/* Hero */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11 }}>
          <View style={{ width: 42, height: 42, borderRadius: 12, backgroundColor: tone.soft, borderWidth: 1, borderColor: tone.line, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${tone.soft}, ${colors.card})` }) }}>
            <SkIcon name="journal" size={19} color={tone.ink} strokeWidth={2} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontFamily: font.monoSemibold, fontSize: 14, fontWeight: '700', color: colors.ink }}>{e.id}</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: tone.soft, borderWidth: 1, borderColor: tone.line }}>
                <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: tone.ink }} />
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 10, fontWeight: '600', color: tone.ink }}>{e.source}</Text>
              </View>
              <View style={{ paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: balanced ? colors.successSoft : colors.warnSoft, borderWidth: 1, borderColor: balanced ? colors.successLine : colors.warnLine }}>
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 10, fontWeight: '600', color: balanced ? colors.success : colors.warn }}>{balanced ? 'Balanced' : 'Unbalanced'}</Text>
              </View>
            </View>
            <Text numberOfLines={2} style={{ fontFamily: font.sans, fontSize: 12.5, color: colors.ink3, marginTop: 3, lineHeight: 17 }}>{e.narration}</Text>
          </View>
        </View>

        {/* Lines table */}
        <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: 11, overflow: 'hidden' }}>
          <View style={{ flexDirection: 'row', backgroundColor: colors.paper2, borderBottomWidth: 1, borderColor: colors.line, paddingVertical: 8, paddingHorizontal: 12, ...web({ backgroundImage: `linear-gradient(180deg, ${colors.paper2}, ${colors.paper})` }) }}>
            <Text style={{ flex: 1, fontFamily: font.sansSemibold, fontSize: 10, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Account</Text>
            <Text style={{ width: 96, textAlign: 'right', fontFamily: font.sansSemibold, fontSize: 10, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Debit</Text>
            <Text style={{ width: 96, textAlign: 'right', fontFamily: font.sansSemibold, fontSize: 10, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Credit</Text>
          </View>
          {e.lines.map((l, i) => {
            const d = money(l.debit), c = money(l.credit);
            return (
              <View key={i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderColor: colors.line }}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontSize: 12.5, fontWeight: '500', color: colors.ink }}>{acctLabel(l.account_id)}</Text>
                  {!!acctCode(l.account_id) && <Text style={{ fontFamily: font.mono, fontSize: 10.5, color: colors.ink4, marginTop: 1 }}>Code {acctCode(l.account_id)}</Text>}
                </View>
                <Text style={{ width: 96, textAlign: 'right', fontFamily: font.mono, fontSize: 12.5, color: d ? colors.ink : colors.ink4 }}>{d ? fmtMoney(d, cur) : '—'}</Text>
                <Text style={{ width: 96, textAlign: 'right', fontFamily: font.mono, fontSize: 12.5, color: c ? colors.success : colors.ink4 }}>{c ? fmtMoney(c, cur) : '—'}</Text>
              </View>
            );
          })}
          <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 11, paddingHorizontal: 12, backgroundColor: colors.paper2 }}>
            <Text style={{ flex: 1, fontFamily: font.sansSemibold, fontSize: 11, fontWeight: '600', color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.4 }}>Totals</Text>
            <Text style={{ width: 96, textAlign: 'right', fontFamily: font.monoSemibold, fontSize: 13, fontWeight: '700', color: colors.ink }}>{fmtMoney(debitTotal, cur)}</Text>
            <Text style={{ width: 96, textAlign: 'right', fontFamily: font.monoSemibold, fontSize: 13, fontWeight: '700', color: colors.ink }}>{fmtMoney(creditTotal, cur)}</Text>
          </View>
        </View>

        {/* Meta */}
        <View style={{ flexDirection: 'row', gap: 9, flexWrap: 'wrap' }}>
          <View style={{ flex: 1, minWidth: 150, padding: 11, borderRadius: 11, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper }}>
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 9.5, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.7 }}>Entry date</Text>
            <Text style={{ fontFamily: font.monoMedium, fontSize: 13, color: colors.ink, marginTop: 3 }}>{e.date || '—'}</Text>
          </View>
          <View style={{ flex: 1, minWidth: 150, padding: 11, borderRadius: 11, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper }}>
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 9.5, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.7 }}>Recorded on</Text>
            <Text style={{ fontFamily: font.monoMedium, fontSize: 13, color: colors.ink, marginTop: 3 }}>{e.createdAt ? e.createdAt.slice(0, 10) : '—'}</Text>
          </View>
        </View>
        {(e.reverses || e.reasonCode) && (
          <View style={{ padding: 11, borderRadius: 11, borderWidth: 1, borderColor: colors.warnLine, backgroundColor: colors.warnSoft }}>
            <Text style={{ fontFamily: font.sansMedium, fontSize: 12, color: colors.warn }}>
              {e.reverses ? `Reverses entry ${e.reverses.slice(0, 8)}` : ''}{e.reverses && e.reasonCode ? ' · ' : ''}{e.reasonCode ? `Reason: ${e.reasonCode}` : ''}
            </Text>
          </View>
        )}
      </View>
    </ModalShell>
  );
};

export default Journals;
